import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Dropdown,
  Messages,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useServices } from "../../hooks/useServices";
import { useMasters } from "../../hooks/useMasters";
import { AppContext } from "../../context/index";
import { mastersFormatter } from "../../utils";
import { Controller, useForm, useWatch } from "react-hook-form";
import { PointsServiceSchema } from "../../validations/schemas";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { usePointsParams } from "../../hooks/usePointsParams";
import { useSchedules } from "../../hooks/useSchedules";
import { useMessages } from "iuvity-ui-components/dist/hooks";
import { useUploadAndDownload } from "../../hooks/useUploadAndDownload";

const formBlankValues = {
  typeOfPointAttention: "",
  country: "",
  services: "",
  city: "",
  typeOfSchedule: "",
};

export const DownloadGeneralPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, language },
    setAppState,
  } = useContext(AppContext);
  
  const {
    control,
    getValues,
    setValue,
    formState: { isDirty },
    reset,
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const typeOfPointAttentionValues = useWatch({
    control: control,
    name: "typeOfPointAttention",
  });

  const countryValues = useWatch({
    control: control,
    name: "country",
  });

  const servicesValues = useWatch({
    control: control,
    name: "services",
  });

  const cityValues = useWatch({
    control: control,
    name: "city",
  });
  
  const typeOfScheduleValues = useWatch({
    control: control,
    name: "typeOfSchedule",
  });

  const { handleMessage, messageRef } = useMessages()
  const [cities, setCities] = useState([])
  const [services, setServices] = useState([])
  const { listCountries, listCities } = usePointsParams();
  const { dataService } = useServices("DATA");
  const { dataMaster } = useMasters("DATA");
  const { dataSchedule } = useSchedules("DATA", "no-translate");
  const { uploadAndDownloadAction } = useUploadAndDownload("DATA");
  const formKeysOptions:any = {
    typeOfPointAttention: mastersFormatter(dataMaster),
    country: listCountries,
    services: services,
    city: cities,
    typeOfSchedule: dataSchedule
    ?.reduce((acc:any, item:any)=>{
      const arrAux = acc.map((obj:any) => obj.value)
      if(!arrAux.includes(item.scheduleTypeId)){
        acc.push({
          label: t(`PointsOfAttention.typeOfSchedule${item.scheduleType.name === 'NORMAL' ? 'REGULAR' : item.scheduleType.name}`),
          value: item.scheduleTypeId,
        });
      }
      return acc;
    },[])
    .sort((a: any, b: any) => a.label.localeCompare(b.label)),
  };

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedPointsObject: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    reset(formBlankValues);
    await uploadAndDownloadAction("DOWNLOAD", formValues);
  };

  const handleModalProps = (actionType: "action" | "cancel", goHome?: boolean) => {
    switch (actionType) {
    case "action":
        return setModalProps((prev) => ({
        ...prev,
        visible: true,
        title: t(`PointsOfAttention.createDownloadModalTitle`),
        content: t(`PointsOfAttention.createDownloadModalContent`),
        onConfirm: onAction,
        }));
    case "cancel":
        return setModalProps((prev) => ({
        ...prev,
        visible: true,
        title: t("Users.cancelModalTitle"),
        content: t("Users.cancelModalContent"),
        onConfirm: goHome ? () => navigate('/') : onReturn,
        }));
    default:
        return;
    }
  };

  const onClearFilters = () => {
    reset(formBlankValues);
  };

  useEffect(()=> {
    if(typeOfPointAttentionValues !== "") {
      setServices(dataService.reduce((acc:any, item:any)=>{
        const arrAux = acc.map((obj:any) => obj.value)
        if(item?.pointServiceTypeId === typeOfPointAttentionValues && !arrAux.includes(item.id)){
          acc.push({
            label: item.name,
            value: item.id,
          });
        }
        return acc;
      },[])
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label)))
    }
    if(countryValues !== "") {
      setCities(listCities.reduce((acc:any, item:any)=>{
        const arrAux = acc.map((obj:any) => obj.value)
        if(item?.countryId === countryValues && !arrAux.includes(item.value)){
          acc.push({
            label: item.label,
            value: item.value,
          });
        }
        return acc;
      },[])
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label)))
    }
  }, [countryValues, typeOfPointAttentionValues])

  useEffect(() => {
    if (language) {
      handleMessage(t('PointsOfAttention.messageDownloadGeneral'), "info")
    }
  }, [language])
  

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => isDirty ? handleModalProps("cancel") : onReturn()}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
              {t("PointsOfAttention.downloadGeneralTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => isDirty ? handleModalProps("cancel") : navigate('/'),
          }}
          breadcrumbsItems={[
            {
              label: t("PointsOfAttention.pointsTitle"),
              command: () => isDirty ? handleModalProps("cancel") : onReturn(),
            },
            {
              label: t(`PointsOfAttention.downloadGeneralBreadcrumb`),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex px-0 pt-5 pb-0"
        variant="tertiary"
      >
        <p>{t("PointsOfAttention.downloadGeneralDescription")}</p>
      </BoxFull>

      <div style={{
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Messages
            ref={messageRef}
            style={{ 
              display: 'block',
              marginTop: '2rem',
              marginBottom: '1.5rem',
            }}
            className={'ibp-message'}
          />
      </div>
      
      <BoxFull
        className="flex align-items-center justify-content-center px-0 py-0"
        variant="tertiary"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(formBlankValues)
                  .map((key, index) => (
                    <div
                      key={`c-${key}`}
                      className={`flex flex-column mb-3 px-6 ${(key === 'services' && servicesValues === '' && !typeOfPointAttentionValues) || 
                      (key === 'city' && cityValues === '' && !countryValues) ? 'p-disabled': ''}`}
                      style={{ width: "50%" }}
                    >
                      <Controller
                        name={key as any}
                        control={control}
                        rules={(PointsServiceSchema(t) as any)[key]}
                        render={({ field }) => {
                          field.onChange = (e) => {
                            setValue(field.name, e.target.value, REVALIDATE_FORM_PROPS)
                          };
                          return (
                            // @ts-ignore
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <Dropdown
                                key={key}
                                id={`dropdown-${key}`}
                                className={`dropdown mb-3`}
                                label={t(`PointsOfAttention.${key}Label`)}
                                placeholder={t(`PointsOfAttention.${key}Placeholder`)}
                                options={formKeysOptions[key]}
                                showClear={false}
                                width="20rem"
                                {...field}
                              />
                            </div>
                          );
                        }}
                      />
                    </div>
                  ))}
                </div>
            </div>
          </div>
        </div>
      </BoxFull>

      <BoxFull className="my-5 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-4">
          <Button
            variant="p-button-link"
            label={t("PointsOfAttention.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            label={t("Tenants.clearFilterBtnLabel")}
            variant="p-button-text"
            onClick={onClearFilters}
          />
          <Button
            variant="p-button"
            label={t("PointsOfAttention.downloadBtnLabel")}
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
